import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Truck, Fuel, Plus, Trash2, ArrowRight, Info, ShieldCheck, Gauge, Calculator } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { applyPageSeo } from "@/lib/seo";
import { useToolGate } from "@/hooks/useToolGate";
import { AnonSignupLock, CommunityProNudge } from "@/components/ToolGate";

const PAGE_URL = "https://routehaulpathways.com/ifta-fuel-tax-calculator";
const STORE_KEY = "rh_ifta_free_uses_v1";
const OG_IMAGE = "https://static.prod-images.emergentagent.com/jobs/ad103537-b988-4363-adb5-ef152c7e8f33/images/6fb701624a4274f43addb642a018559db10f61dd82a30647a8d97656c469f4b7.jpeg";
const TITLE = "Free IFTA Fuel Tax Calculator for Truckers & Owner-Operators | RouteHaul";
const DESCRIPTION =
  "Free IFTA fuel tax calculator. Enter your miles and gallons purchased per state to estimate your quarterly International Fuel Tax Agreement liability or credit, jurisdiction by jurisdiction.";

const money = (n) => `${n < 0 ? "-" : ""}$${Math.abs(isFinite(n) ? n : 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const num = (v) => parseFloat(v) || 0;
let RID = 0;
const newRow = (state = "", miles = "", gallons = "", rate = "") => ({ id: ++RID, state, miles, gallons, rate });

export default function IFTACalculator() {
  const { user, isPro, isCommunity, isAnon, locked, anonFreeLeft, registerUse, ready } = useToolGate(STORE_KEY);
  const [results, setResults] = useState(null);
  const [rows, setRows] = useState([
    newRow("California", 3200, 380, 0.68),
    newRow("Nevada", 1800, 320, 0.27),
    newRow("Arizona", 1500, 300, 0.26),
  ]);

  useEffect(() => applyPageSeo({
    title: TITLE,
    description: DESCRIPTION,
    canonical: PAGE_URL,
    image: OG_IMAGE,
    imageAlt: "Free IFTA fuel tax calculator — RouteHaul Logistics Pathways",
    seoKey: "ifta",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      name: "IFTA Fuel Tax Calculator",
      description: DESCRIPTION,
      url: PAGE_URL,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      publisher: { "@type": "Organization", name: "RouteHaul Logistics Pathways", url: "https://routehaulpathways.com/" },
    },
  }), []);

  const update = (id, key, value) => { setResults(null); setRows((rs) => rs.map((r) => (r.id === id ? { ...r, [key]: value } : r))); };
  const addRow = () => { setResults(null); setRows((rs) => [...rs, newRow()]); };
  const removeRow = (id) => { setResults(null); setRows((rs) => (rs.length > 1 ? rs.filter((r) => r.id !== id) : rs)); };

  const compute = () => {
    const totalMiles = rows.reduce((a, r) => a + num(r.miles), 0);
    const totalGallons = rows.reduce((a, r) => a + num(r.gallons), 0);
    const fleetMPG = totalGallons > 0 ? totalMiles / totalGallons : 0;
    const perState = rows.map((r) => {
      const taxableGallons = fleetMPG > 0 ? num(r.miles) / fleetMPG : 0;
      const netGallons = taxableGallons - num(r.gallons);
      const tax = netGallons * num(r.rate);
      return { id: r.id, taxableGallons, netGallons, tax };
    });
    const netTax = perState.reduce((a, r) => a + r.tax, 0);
    return { totalMiles, totalGallons, fleetMPG, perState, netTax };
  };

  const handleCalculate = () => {
    if (locked) return;
    setResults(compute());
    registerUse();
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="ifta-page">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="ifta-brand" className="flex items-center gap-2 font-heading font-black text-lg tracking-tight text-slate-900">
            <Truck className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span>RouteHaul<span className="hidden sm:inline text-slate-400 font-bold"> Logistics Pathways</span></span>
          </Link>
          <div className="flex items-center gap-2 sm:gap-3">
            {user ? (
              <Link to="/" data-testid="ifta-dashboard"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Go to dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" data-testid="ifta-login"><Button variant="ghost" className="h-10 px-4 rounded-full text-slate-700 font-bold text-xs sm:text-sm tracking-wide uppercase hover:bg-slate-100">Log in</Button></Link>
                <Link to="/register" data-testid="ifta-header-signup"><Button className="h-10 px-4 sm:px-5 rounded-full bg-brand hover:bg-brand-hover text-white font-bold text-xs sm:text-sm tracking-wide uppercase">Get started</Button></Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-4">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <div className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-bold text-brand mb-3">
            <Fuel className="w-4 h-4" /> Free owner-operator tool
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl tracking-tighter font-black text-slate-900 leading-[1.02]">
            IFTA Fuel Tax Calculator
          </h1>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Estimate your quarterly IFTA fuel tax before you file. Enter the miles you ran and gallons you
            bought in each state, and we'll calculate your fleet MPG, taxable gallons, and what you owe or get
            credited — jurisdiction by jurisdiction.
          </p>
        </motion.div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid lg:grid-cols-5 gap-8 items-start">
        {/* Inputs */}
        <div className="lg:col-span-3 space-y-4">
          {rows.map((r, i) => {
            const res = results ? results.perState[i] : null;
            return (
              <div key={r.id} className="border border-slate-200 rounded-md p-5 bg-white" data-testid={`ifta-row-${i}`}>
                <div className="flex items-center justify-between mb-4">
                  <Input
                    value={r.state}
                    onChange={(e) => update(r.id, "state", e.target.value)}
                    placeholder="Jurisdiction (e.g. California)"
                    data-testid={`ifta-state-${i}`}
                    className="h-10 font-bold text-slate-900 max-w-[220px]"
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(r.id)}
                    data-testid={`ifta-remove-${i}`}
                    className="text-slate-400 hover:text-red-500 transition-colors p-2"
                    aria-label="Remove jurisdiction"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Miles driven</Label>
                    <Input type="number" min="0" value={r.miles} onChange={(e) => update(r.id, "miles", e.target.value)} data-testid={`ifta-miles-${i}`} className="h-10 font-mono tabular" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Gallons bought</Label>
                    <Input type="number" min="0" value={r.gallons} onChange={(e) => update(r.id, "gallons", e.target.value)} data-testid={`ifta-gallons-${i}`} className="h-10 font-mono tabular" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Tax rate $/gal</Label>
                    <Input type="number" min="0" step="0.001" value={r.rate} onChange={(e) => update(r.id, "rate", e.target.value)} data-testid={`ifta-rate-${i}`} className="h-10 font-mono tabular" />
                  </div>
                </div>
                {res && (() => {
                  const isZero = Math.abs(res.tax) < 0.005;
                  const label = isZero ? "Even " : res.tax > 0 ? "Owe " : "Credit ";
                  const cls = isZero ? "text-slate-500" : res.tax > 0 ? "text-red-600" : "text-emerald-600";
                  return (
                    <div className="mt-3 flex items-center justify-between text-xs border-t border-slate-100 pt-3">
                      <span className="text-slate-500">Net taxable gallons: <span className="font-mono font-bold text-slate-700">{res.netGallons.toFixed(1)}</span></span>
                      <span className={`font-mono font-bold ${cls}`} data-testid={`ifta-row-tax-${i}`}>
                        {label}{money(Math.abs(res.tax))}
                      </span>
                    </div>
                  );
                })()}
              </div>
            );
          })}

          <Button variant="outline" onClick={addRow} data-testid="ifta-add-row" className="h-11 px-5 rounded-full border-slate-300 text-slate-700 font-bold w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" /> Add another state
          </Button>

          <div className="flex items-start gap-2 text-xs text-slate-500 leading-relaxed bg-orange-50/50 border border-orange-100 rounded-md p-4" data-testid="ifta-rate-note">
            <Info className="w-4 h-4 text-brand shrink-0 mt-0.5" />
            <span>
              IFTA tax rates change <strong>every quarter</strong> and differ by state. The rates above are
              examples — replace each one with the current quarter's diesel rate for that jurisdiction from{" "}
              <a href="https://www.iftach.org/" target="_blank" rel="noopener noreferrer" className="text-brand font-bold underline">iftach.org</a>.
            </span>
          </div>

          {!locked ? (
            <div>
              <Button onClick={handleCalculate} data-testid="ifta-calculate-btn" className="h-14 px-8 rounded-full bg-brand hover:bg-brand-hover text-white text-base font-bold w-full sm:w-auto group">
                <Calculator className="w-5 h-5 mr-2" /> Calculate my IFTA
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              {ready && isAnon && anonFreeLeft > 0 && (
                <p className="mt-3 text-sm text-slate-500" data-testid="ifta-free-left">1 free calculation — create a free account for unlimited use</p>
              )}
              {isCommunity && results && (
                <div className="mt-6"><CommunityProNudge testid="ifta-pro-nudge" perk="save your quarterly filings" /></div>
              )}
            </div>
          ) : (
            <AnonSignupLock testid="ifta-anon-lock" perks={[
              "Unlimited IFTA calculations",
              "The full 12-Step Business Foundation Guide",
              "Cost-per-mile, budget & tax tools",
            ]} />
          )}
        </div>

        {/* Results */}
        <div className="lg:col-span-2 lg:sticky lg:top-24">
          {!results ? (
            <div className="border border-dashed border-slate-300 rounded-md p-10 text-center bg-white" data-testid="ifta-empty">
              <Calculator className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Enter your states and hit <span className="font-bold text-slate-700">Calculate</span> to see your IFTA total.</p>
            </div>
          ) : (
            <>
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-slate-900 text-white rounded-md p-8">
                <div className="text-xs tracking-[0.2em] uppercase font-bold text-slate-400 mb-2">Estimated net IFTA {results.netTax >= 0 ? "due" : "credit"}</div>
                <div className={`font-mono font-bold tabular leading-none ${results.netTax >= 0 ? "text-brand" : "text-emerald-400"}`} data-testid="ifta-net-tax" style={{ fontSize: "clamp(3rem, 7vw, 4.5rem)" }}>
                  {money(Math.abs(results.netTax))}
                </div>
                <div className="text-slate-500 text-sm mt-1">{results.netTax >= 0 ? "you likely owe this quarter" : "credit toward this quarter"}</div>

                <div className="mt-8 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300 inline-flex items-center gap-1.5"><Gauge className="w-4 h-4 text-brand" /> Fleet MPG</span>
                    <span className="font-mono tabular font-bold text-white" data-testid="ifta-fleet-mpg">{results.fleetMPG.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Total miles</span>
                    <span className="font-mono tabular font-bold text-white">{results.totalMiles.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-300">Total gallons purchased</span>
                    <span className="font-mono tabular font-bold text-white">{results.totalGallons.toLocaleString()}</span>
                  </div>
                </div>

                <div className="mt-6 flex items-start gap-2 text-xs text-slate-400 leading-relaxed">
                  <Info className="w-4 h-4 text-brand shrink-0 mt-0.5" />
                  We divide your total miles by total gallons to get fleet MPG, then charge each state's tax rate on the gallons you burned there minus the gallons you bought there.
                </div>
              </motion.div>
              <p className="text-xs text-slate-400 mt-4 text-center">Estimate only — confirm with your base state IFTA filing.</p>
            </>
          )}
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-brand text-white">
        <div className="max-w-3xl mx-auto px-6 py-14 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tighter font-black leading-tight">Stop dreading quarterly filings</h2>
          <p className="mt-4 text-orange-50 text-lg">IFTA is just one piece. Create a free account for the 12-Step Business Foundation Guide — including how to set up ELD + IFTA the right way — plus a searchable glossary and readiness diagnostic.</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="ifta-band-signup">
              <Button className="h-14 px-8 rounded-full bg-white text-brand hover:bg-orange-50 text-base font-bold group w-full sm:w-auto">
                Start the free guide
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/cost-per-mile-calculator" data-testid="ifta-band-cpm">
              <Button variant="outline" className="h-14 px-8 rounded-full border-white/60 bg-transparent text-white hover:bg-white/10 text-base font-bold w-full sm:w-auto">
                Free cost-per-mile calculator
              </Button>
            </Link>
          </div>
          <p className="mt-6 inline-flex items-center gap-2 text-sm text-orange-50/90">
            <ShieldCheck className="w-4 h-4" /> Educational mentorship — not tax advice.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-8 text-sm text-slate-400 flex items-center justify-between flex-wrap gap-2">
          <span>© {new Date().getFullYear()} RouteHaul Logistics Pathways</span>
          <div className="flex items-center gap-4">
            <Link to="/" className="hover:text-slate-900">Home</Link>
            <Link to="/cost-per-mile-calculator" className="hover:text-slate-900">CPM Calculator</Link>
            <Link to="/how-much-do-owner-operators-make" className="hover:text-slate-900">Owner-Operator Pay</Link>
            <Link to="/owner-operator-faq" className="hover:text-slate-900">FAQ</Link>
            <Link to="/trucking-glossary" className="hover:text-slate-900">Glossary</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
